/**
 * Normalizes text by collapsing multiple spaces and removing extra whitespace
 */
export const normalizeWhitespace = (str: string): string => {
  return str.replace(/\s+/g, " ").trim();
};

/**
 * Fast fuzzy match with limited edit distance (max 3 chars different)
 * Uses optimized approach for performance
 */
export const findFuzzyMatch = (
  text: string,
  highlight: string,
  maxEditDistance: number = 3
): { start: number; end: number } | null => {
  if (!text || !highlight) return null;

  const k = Math.min(maxEditDistance, 3);

  // Normalize and lowercase once
  const normalizedText = normalizeWhitespace(text.toLowerCase());
  const normalizedHighlight = normalizeWhitespace(highlight.toLowerCase());

  // Exact first
  const exactIndex = normalizedText.indexOf(normalizedHighlight);
  if (exactIndex !== -1) {
    return mapNormalizedPositionToOriginal(
      text,
      normalizedText,
      exactIndex,
      normalizedHighlight.length
    );
  }

  const n = normalizedText.length;
  const m = normalizedHighlight.length;
  if (m === 0) return null;

  let bestStart = -1;
  let bestEnd = -1;
  let bestDist = Infinity;

  // Slide over normalized text; allow length variance within k
  for (let i = 0; i <= n - m + k; i++) {
    for (let lenOffset = -k; lenOffset <= k; lenOffset++) {
      const len = m + lenOffset;
      if (len < 1) continue;
      if (i + len > n) continue;

      let dist = -1;

      if (len === m) {
        // Fast Hamming with early exit
        dist = hammingWithinK(normalizedText, i, normalizedHighlight, k);
      }

      if (dist === -1) {
        // Banded Levenshtein at position i without substring allocation
        dist = bandedEditDistanceAt(
          normalizedText,
          i,
          len,
          normalizedHighlight,
          k
        );
      }

      if (dist !== -1 && dist < bestDist) {
        bestDist = dist;
        bestStart = i;
        bestEnd = i + len;

        if (dist <= 1) {
          // Good enough — map and return
          return mapNormalizedPositionToOriginal(
            text,
            normalizedText,
            bestStart,
            bestEnd - bestStart
          );
        }
      }
    }
  }

  if (bestStart !== -1) {
    return mapNormalizedPositionToOriginal(
      text,
      normalizedText,
      bestStart,
      bestEnd - bestStart
    );
  }
  return null;
};

// Cheap Hamming distance for equal-length windows (returns -1 if > k)
function hammingWithinK(
  haystack: string,
  start: number,
  needle: string,
  k: number
): number {
  let diff = 0;
  for (let j = 0; j < needle.length; j++) {
    if (haystack.charCodeAt(start + j) !== needle.charCodeAt(j)) {
      if (++diff > k) return -1;
    }
  }
  return diff;
}

// Banded Levenshtein distance computed directly at (start, len) without slicing
// Returns -1 if distance exceeds k
function bandedEditDistanceAt(
  haystack: string,
  start: number,
  len: number,
  needle: string,
  k: number
): number {
  const m = len;
  const n = needle.length;

  if (Math.abs(m - n) > k) return -1;
  if (m === 0) return n <= k ? n : -1;
  if (n === 0) return m <= k ? m : -1;

  const prev = new Uint16Array(n + 1);
  const curr = new Uint16Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  const INF = k + 1;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;

    const jStart = Math.max(1, i - k);
    const jEnd = Math.min(n, i + k);

    for (let j = 1; j < jStart; j++) curr[j] = INF;

    let minInRow = curr[0];
    const hayCh = haystack.charCodeAt(start + i - 1);

    for (let j = jStart; j <= jEnd; j++) {
      const cost = hayCh === needle.charCodeAt(j - 1) ? 0 : 1;
      const del = prev[j] + 1;
      const ins = curr[j - 1] + 1;
      const sub = prev[j - 1] + cost;

      // Branchless minimum of three values
      const v = del < ins ? (del < sub ? del : sub) : ins < sub ? ins : sub;
      curr[j] = v;
      if (v < minInRow) minInRow = v;
    }

    for (let j = jEnd + 1; j <= n; j++) curr[j] = INF;

    if (minInRow > k) return -1;
    prev.set(curr);
  }

  return prev[n] <= k ? prev[n] : -1;
}

/**
 * Maps a position from normalized text back to original text
 */
function mapNormalizedPositionToOriginal(
  originalText: string,
  normalizedText: string,
  normalizedStart: number,
  normalizedLength: number
): { start: number; end: number } {
  let originalPos = 0;
  let normalizedPos = 0;
  let startPos = -1;
  let endPos = -1;

  for (
    let i = 0;
    i < originalText.length &&
    normalizedPos <= normalizedStart + normalizedLength;
    i++
  ) {
    if (normalizedPos === normalizedStart && startPos === -1) {
      startPos = i;
    }
    if (normalizedPos === normalizedStart + normalizedLength) {
      endPos = i;
      break;
    }

    // Skip consecutive whitespace in original text
    if (
      originalText[i].match(/\s/) &&
      i + 1 < originalText.length &&
      originalText[i + 1].match(/\s/)
    ) {
      continue;
    }
    normalizedPos++;
  }

  if (startPos !== -1) {
    if (endPos === -1) {
      endPos = Math.min(startPos + normalizedLength, originalText.length);
    }
    return { start: startPos, end: endPos };
  }

  return { start: 0, end: normalizedLength };
}

/**
 * Fast edit distance calculation with early termination
 * Returns -1 if distance exceeds threshold
 */
function fastEditDistance(
  str1: string,
  str2: string,
  maxDistance: number
): number {
  const m = str1.length;
  const n = str2.length;

  // Quick checks
  if (Math.abs(m - n) > maxDistance) return -1;
  if (m === 0) return n <= maxDistance ? n : -1;
  if (n === 0) return m <= maxDistance ? m : -1;

  // Use only two rows instead of full matrix for space efficiency
  let prevRow = Array(n + 1).fill(0);
  let currRow = Array(n + 1).fill(0);

  // Initialize first row
  for (let j = 0; j <= n; j++) {
    prevRow[j] = j;
  }

  for (let i = 1; i <= m; i++) {
    currRow[0] = i;
    let minInRow = i;

    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        currRow[j] = prevRow[j - 1];
      } else {
        currRow[j] = Math.min(
          prevRow[j] + 1, // deletion
          currRow[j - 1] + 1, // insertion
          prevRow[j - 1] + 1 // substitution
        );
      }
      minInRow = Math.min(minInRow, currRow[j]);
    }

    // Early termination: if minimum in current row exceeds threshold, abort
    if (minInRow > maxDistance) return -1;

    // Swap rows
    [prevRow, currRow] = [currRow, prevRow];
  }

  return prevRow[n] <= maxDistance ? prevRow[n] : -1;
}
